import React, { useEffect, useState } from 'react';
import { downloadFile, parseErrorResponse, UploadError } from '../../utils/fileUtils';
import {
    Box,
    Button,
    Typography,
    Modal,
    TextField,
    CircularProgress,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiClient from '../../axiosClient';
import MonthYearSelector from './monthYearSelector';
import { currentPoolMonth } from '../../utils/commonUtils';
import ErrorDisplay from './errorDisplay';
import DropDown from './dropdown';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
};

interface CsvErrorData {
    filename: string;
    data: Blob;
}

interface UploadProp {
    label: string,
    uploadApi: string,
    uploadType: string,
    uploadFormat?: string;
    open: boolean;
    onClose: () => void;
}

const UploadComponent: React.FC<UploadProp> = ({ label, uploadApi, uploadType, uploadFormat, open, onClose }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState<UploadError | null>(null);
    const [csvErrorData, setCsvErrorData] = useState<CsvErrorData | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [poolMonth, setPoolMonth] = useState<string>(currentPoolMonth());
    const [selectedLoanType, setSelectedLoanType] = useState<string>('');
    const [loanTypes, setLoanTypes] = useState<string[]>([]);
    const picklists = useSelector((state: RootState) => state.picklists.picklists);

    useEffect(() => {
        setSuccessMessage(null);
        // Initialize loan types for BulkUpload
        if (uploadType === "BulkUpload" && picklists?.LOAN_TYPE) {
            const availableLoanTypes = picklists.LOAN_TYPE.filter(loanType => loanType.trim() !== '');
            setLoanTypes(availableLoanTypes);
        }
    }, [picklists]);

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadClick = async () => {
        if (!selectedFile) {
            setError({
                type: 'Validation',
                message: 'Please select a file before uploading.',
                details: []
            });
            return;
        }

        if ((uploadType === "PaymentFile" || uploadType === "BulkUpload") && !poolMonth) {
            setError({
                type: 'Validation',
                message: 'Please select a pool month.',
                details: []
            });
            return;
        }

        if (uploadType === "BulkUpload" && !selectedLoanType) {
            setError({
                type: 'Validation',
                message: 'Please select a loan type.',
                details: []
            });
            return;
        }

        setIsUploading(true);
        setError(null);
        setSuccessMessage(null);
        setCsvErrorData(null);

        const formData = new FormData();
        formData.append('file', selectedFile);

        let apiUrl = uploadApi;
        if (uploadType === "PaymentFile") {
            apiUrl += "?pool_month=" + poolMonth;
        } else if (uploadType === "BulkUpload") {
            apiUrl += `?pool_month=${poolMonth}&loan_type=${selectedLoanType}`;
        }

        try {
            const response = await apiClient.post(apiUrl, formData, {
                responseType: 'blob',
                validateStatus: () => true
            });

            if (response.status === 200) {
                if (uploadType === "PaymentFile") {
                    downloadFile(new Blob([response.data]), 'data.txt');
                }
                setSuccessMessage("file uploaded successfully");
            } else if (response.status === 422 || response.status === 400) {
                // Handle validation errors
                const contentType = response.headers['content-type'];
                const contentDisposition = response.headers['content-disposition'];
                const isCsv = (contentType && contentType.includes('text/csv')) ||
                    (contentDisposition && contentDisposition.includes('attachment'));

                if (isCsv) {
                    setCsvErrorData({
                        filename: 'incorrect_values.csv',
                        data: new Blob([response.data], { type: 'text/csv' })
                    });
                    setError({
                        type: 'Validation',
                        message: 'Upload failed. Please download the file incorrect_values.csv for validation errors and check.',
                        details: ['The uploaded file contains invalid data. Please download and review the CSV file for errors before uploading again.']
                    });
                } else {
                    const text = await response.data.text();
                    const parsedError = parseErrorResponse(text, 'Validation', 'Upload failed. Please download the file unavailable_values.csv and check.');
                    setError(parsedError);
                }
            } else {
                // Handle other errors
                const contentType = response.headers['content-type'];
                const text = await response.data.text();
                const parsedError = parseErrorResponse(text, 'Upload', 'File upload failed.');
                setError(parsedError);
            }
        } catch (error: any) {
            if (error.response && error.response.data) {
                const parsedError = parseErrorResponse(error.response.data, 'Unknown', 'An unexpected error occurred.');
                setError(parsedError);
            } else if (error.request) {
                setError({
                    type: 'Network',
                    message: 'No response received from server.',
                    details: []
                });
            } else {
                setError({
                    type: 'Unknown',
                    message: 'An unexpected error occurred.',
                    details: error.message
                });
            }
        } finally {
            setIsUploading(false);
        }
    };

    const handleModalClose = () => {
        setSelectedFile(null);
        setError(null);
        setSuccessMessage(null);
        setCsvErrorData(null);
        setIsUploading(false);
        setSelectedLoanType('');
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleModalClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
        >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography id="modal-title" variant="h6">
                        {label}
                    </Typography>
                    <IconButton onClick={handleModalClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Box display="flex" flexDirection="row" justifyContent="space-between">
                    <Box width={"40%"}>
                    {(uploadType === "PaymentFile" || uploadType === "BulkUpload") && <MonthYearSelector onDateSelection={setPoolMonth} />}
                    </Box>
                    <Box width={"40%"}>
                    {uploadType === "BulkUpload" && (
                        <Box mb={2}>
                            <DropDown
                                label="Loan Type"
                                items={loanTypes}
                                selectedValue={selectedLoanType}
                                onSelection={setSelectedLoanType}
                                height={45}
                                />
                        </Box>
                    )}
                    </Box>
                </Box>


                <Box mt={2} mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        Select an Excel file (.xlsx)
                    </Typography>
                </Box>

                {uploadFormat && (
                    <Box mb={2}>
                        <Typography>File Upload Format:-</Typography>
                        <Typography>{uploadFormat}</Typography>
                    </Box>
                )}

                {/* File Input */}
                <Box mt={1} mb={2}>
                    <TextField
                        type="file"
                        fullWidth
                        slotProps={{ htmlInput: { accept: '.xlsx' } }}
                        onChange={handleFileChange}
                        sx={{
                            '& input': {
                                padding: '8px',
                            },
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                        }}
                    />
                </Box>

                {/* Display selected file name */}
                {selectedFile && (
                    <Box mb={2}>
                        <Typography variant="body1" fontWeight="bold">
                            Selected file: {selectedFile.name}
                        </Typography>
                    </Box>
                )}

                {/* Error Message */}
                {error && <ErrorDisplay error={error} csvDownloadData={csvErrorData} />}

                {successMessage && (
                    <Box mb={2}>
                        <Typography variant="body2" color="success">
                            {successMessage}
                        </Typography>
                    </Box>
                )}

                {/* Upload Button */}
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadClick}
                        disabled={isUploading}
                        sx={{
                            bgcolor: '#1976d2',
                            color: 'white',
                            fontWeight: 'bold',
                            '&:hover': {
                                bgcolor: '#1565c0',
                            },
                        }}
                    >
                        {isUploading ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default UploadComponent;
